import { useState, useRef, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { MARK_NOTIFICATIONS_AS_SEEN } from "../../../apollo-client/mutations";

/**
 * Shared hook for common notification click handling logic
 * Handles double-click prevention, timeout management, and marking notifications as seen
 */
export const useNotificationClick = (notificationId, setIsNotificationsMenuOpen) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const clickTimeoutRef = useRef(null);
  const [markNotificationsAsSeen] = useMutation(MARK_NOTIFICATIONS_AS_SEEN);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const markNotificationAsSeen = () => {
    markNotificationsAsSeen({ variables: { ids: [notificationId] } });
    setIsNotificationsMenuOpen(false);
  };

  const createClickHandler = (customAction, resetDelay = 100) => {
    return (event) => {
      // Prevent double-clicks and ensure single execution
      if (isProcessing) {
        return;
      }
      
      // Clear any existing timeout
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      
      setIsProcessing(true);
      
      // Prevent any default behavior and stop propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      markNotificationAsSeen();
      
      // Execute custom action
      if (customAction) {
        customAction();
      }
      
      // Reset processing state after specified delay
      clickTimeoutRef.current = setTimeout(() => setIsProcessing(false), resetDelay);
    };
  };

  return {
    isProcessing,
    createClickHandler,
  };
};
